<template>
  <div class="mma-overlay" @click.self="$emit('close')">
    <div class="mma-dialog card">

      <!-- ── Step: select ─────────────────────────────────────────────── -->
      <template v-if="step === 'select'">
        <div class="mma-header">
          <h5 class="mb-0">إضافة أعضاء إلى المجموعة</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>

        <div class="mma-body">
          <input
            type="text"
            class="form-control mb-2"
            placeholder="بحث بالاسم أو رقم ولي الأمر"
            v-model="search"
          />

          <div class="d-flex flex-wrap gap-2 mb-2">
            <button class="btn btn-sm btn-outline-primary" @click="selectRemaining">تحديد الأعضاء المتبقين</button>
            <button class="btn btn-sm btn-outline-secondary" @click="clearSelection">مسح التحديد</button>
            <button class="btn btn-sm btn-outline-primary" @click="selectVisible">تحديد الظاهرين</button>
          </div>

          <div v-if="loadingCandidates" class="text-center py-4">
            <i class="pi pi-spin pi-spinner"></i>
          </div>
          <div v-else-if="loadError" class="text-danger py-3">{{ loadError }}</div>
          <div v-else class="mma-list">
            <div v-if="filteredCandidates.length === 0" class="text-muted text-center py-3">لا يوجد طلاب مطابقون</div>
            <div
              v-for="c in filteredCandidates"
              :key="c.student_id"
              class="mma-row"
              :class="{ 'mma-row--disabled': !isSelectable(c) }"
            >
              <input
                type="checkbox"
                :checked="selectedIds.has(c.student_id)"
                :disabled="!isSelectable(c)"
                @change="toggle(c.student_id)"
              />
              <span class="mma-name">{{ c.name }}</span>
              <span class="mma-phone text-muted">{{ c.parent_phone || '—' }}</span>
              <span class="status-badge" :class="rowBadge(c).class">
                {{ rowBadge(c).text }}
              </span>
              <span class="mma-digits-badge">
                {{ phoneDigitsBadge(c).dot }} {{ phoneDigitsBadge(c).text }}
              </span>
            </div>
          </div>
        </div>

        <div class="mma-footer">
          <span>محدد: <strong>{{ selectedIds.size }}</strong> طالب</span>
          <div class="d-flex gap-2">
            <button class="btn btn-outline-secondary" @click="$emit('close')">إلغاء</button>
            <button class="btn PrimaryButton" :disabled="selectedIds.size === 0" @click="step = 'settings'">إضافة المحددين</button>
          </div>
        </div>
      </template>

      <!-- ── Step: batch settings confirmation ───────────────────────── -->
      <template v-else-if="step === 'settings'">
        <div class="mma-header">
          <h5 class="mb-0">إعدادات الإضافة</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>

        <div class="mma-body">
          <p>سيتم إضافة <strong>{{ selectedIds.size }}</strong> طالب على دفعات صغيرة.</p>

          <div class="mb-3">
            <label class="form-label">حجم الدفعة</label>
            <select class="form-control" v-model.number="batchSize">
              <option v-for="n in [1, 2, 3, 5, 10]" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>

          <div class="mb-3">
            <label class="form-label">التأخير بين الدفعات (ثوانٍ)</label>
            <select class="form-control" v-model.number="delaySeconds">
              <option v-for="n in [3, 5, 10, 15]" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>
        </div>

        <div class="mma-footer">
          <button class="btn btn-outline-secondary" @click="step = 'select'">رجوع</button>
          <button class="btn PrimaryButton" @click="startAdding">بدء الإضافة</button>
        </div>
      </template>

      <!-- ── Step: progress ───────────────────────────────────────────── -->
      <template v-else-if="step === 'progress'">
        <div class="mma-header">
          <h5 class="mb-0">جارٍ إضافة الأعضاء</h5>
        </div>

        <div class="mma-body">
          <div class="d-flex justify-content-between mb-1">
            <span>تمت الإضافة: <strong>{{ addedCount }} / {{ selectedTotal }}</strong></span>
            <span>الدفعة الحالية: <strong>{{ currentBatchIndex }} / {{ totalBatches }}</strong></span>
          </div>
          <div class="progress mb-3" style="height: 8px;">
            <div class="progress-bar" role="progressbar" :style="{ width: progressPercent + '%' }"></div>
          </div>

          <div class="mma-log">
            <div v-for="(line, i) in log" :key="i" :class="'mma-log-line mma-log-line--' + line.type">
              {{ line.icon }} {{ line.text }}
            </div>
          </div>
        </div>
      </template>

      <!-- ── Step: summary ────────────────────────────────────────────── -->
      <template v-else-if="step === 'summary'">
        <div class="mma-header">
          <h5 class="mb-0">اكتملت العملية</h5>
          <button type="button" class="btn-close" @click="finish"></button>
        </div>

        <div class="mma-body">
          <div class="mma-summary-row">
            <span>تمت الإضافة بنجاح</span>
            <strong class="text-success">{{ addedCount }}</strong>
          </div>
          <div class="mma-summary-row" v-if="alreadyInGroupCount">
            <span>موجود بالمجموعة بالفعل</span>
            <strong>{{ alreadyInGroupCount }}</strong>
          </div>
          <div class="mma-summary-row" v-if="invalidPhoneCount">
            <span>رقم غير صالح</span>
            <strong class="text-danger">{{ invalidPhoneCount }}</strong>
          </div>
          <div class="mma-summary-row" v-if="noWhatsappCount">
            <span>غير مسجَّل على واتساب</span>
            <strong class="text-danger">{{ noWhatsappCount }}</strong>
          </div>
          <div class="mma-summary-row" v-if="privacyRestrictedCount">
            <span>تقييد خصوصية</span>
            <strong class="text-warning">{{ privacyRestrictedCount }}</strong>
          </div>
          <div class="mma-summary-row" v-if="checkFailedCount">
            <span>تعذّر التحقق من الرقم</span>
            <strong class="text-warning">{{ checkFailedCount }}</strong>
          </div>
          <div class="mma-summary-row">
            <span>فشل</span>
            <strong class="text-danger">{{ failedCount }}</strong>
          </div>
          <div class="mma-summary-row">
            <span>تم تخطيه</span>
            <strong class="text-muted">{{ skippedCount }}</strong>
          </div>

          <div class="mma-log mt-3">
            <div v-for="(line, i) in log" :key="i" :class="'mma-log-line mma-log-line--' + line.type">
              {{ line.icon }} {{ line.text }}
            </div>
          </div>
        </div>

        <div class="mma-footer">
          <span></span>
          <button class="btn PrimaryButton" @click="finish">تم</button>
        </div>
      </template>

    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

// Manual Member Adder — completely independent of GroupQueue/Recovery/Resume/
// Cancel/Create Group. Drives its own small-batch + delay loop against the
// direct (non-queue) add-participants endpoint added for this feature.
function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export default {
  name: 'ManualMemberAdderDialog',
  props: {
    groupId: { type: [String, Number], required: true },
  },
  emits: ['close', 'completed'],

  data() {
    return {
      step: 'select', // select -> settings -> progress -> summary

      loadingCandidates: true,
      loadError: '',
      candidates: [],
      search: '',
      selectedIds: new Set(),

      batchSize: 5,
      delaySeconds: 5,

      // Progress state — mirrors the backend's full outcome taxonomy
      // (added/already_in_group/invalid_phone/no_whatsapp/privacy_restricted/
      // failed), plus 'skipped' for batches never attempted because the
      // admin chose to stop after a batch-level problem.
      addedCount: 0,
      alreadyInGroupCount: 0,
      invalidPhoneCount: 0,
      noWhatsappCount: 0,
      privacyRestrictedCount: 0,
      failedCount: 0,
      checkFailedCount: 0,
      skippedCount: 0,
      currentBatchIndex: 0,
      totalBatches: 0,
      selectedTotal: 0,
      log: [],
    };
  },

  computed: {
    filteredCandidates() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.candidates;
      return this.candidates.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.parent_phone || '').toLowerCase().includes(q)
      );
    },
    progressPercent() {
      if (this.selectedTotal === 0) return 0;
      const processed = this.addedCount + this.alreadyInGroupCount + this.invalidPhoneCount
        + this.noWhatsappCount + this.privacyRestrictedCount + this.failedCount
        + this.checkFailedCount + this.skippedCount;
      return Math.round((processed / this.selectedTotal) * 100);
    },
  },

  methods: {
    async fetchCandidates() {
      this.loadingCandidates = true;
      this.loadError = '';
      try {
        const { data } = await axios.get(`/student-groups/${this.groupId}/whatsapp/manual-add/candidates`);
        if (!data.status) {
          this.loadError = data.message || 'تعذر تحميل قائمة الطلاب';
          return;
        }
        this.candidates = data.data?.candidates ?? [];
      } catch (error) {
        this.loadError = error.response?.data?.message || 'حدث خطأ أثناء التحميل';
      } finally {
        this.loadingCandidates = false;
      }
    },

    // Single source of truth for "can this row be checked at all" — a
    // student already in the group, OR whose phone doesn't pass validation
    // (yellow or red), is never selectable. Only a green (exactly 11 digits,
    // starts with 01) + not-already-a-member candidate is.
    isSelectable(c) {
      return !c.already_in_group && c.phone_validation?.level === 'green';
    },

    // Phone validation (green/yellow/red) takes priority in the badge when
    // the number itself isn't ready — showing "already in group" alongside
    // an invalid-phone badge would be confusing/redundant, and in practice a
    // student with an invalid number was never a group member via that
    // number anyway.
    rowBadge(c) {
      if (c.phone_validation?.level !== 'green') {
        const cls = c.phone_validation?.level === 'yellow' ? 'status-badge--pending' : 'status-badge--rejected';
        return { class: cls, text: c.phone_validation?.label || 'رقم غير صالح' };
      }
      return c.already_in_group
        ? { class: 'status-badge--accepted', text: 'موجود بالمجموعة بالفعل' }
        : { class: 'status-badge--pending', text: 'جاهز للإضافة' };
    },

    // UI-only — computed live from c.parent_phone on every render, never
    // cached, so it updates automatically if the displayed phone changes.
    // Digit count is counted fresh here (numeric characters only, everything
    // else — spaces/+/-/() — stripped); the red/yellow/green color dot reuses
    // c.phone_validation.level exactly as already computed by the backend,
    // so this never contradicts the existing status badge/selection rules.
    phoneDigitsBadge(c) {
      const digits = String(c.parent_phone || '').replace(/\D/g, '');
      const level = c.phone_validation?.level;
      const dot = level === 'green' ? '🟢' : level === 'yellow' ? '🟡' : '🔴';

      if (digits.length === 0) {
        return { dot, text: 'لا يوجد رقم' };
      }
      if (level === 'green') {
        return { dot, text: `${c.phone_validation.label} • ${digits.length} رقمًا` };
      }
      return { dot, text: `${digits.length} رقمًا` };
    },

    toggle(studentId) {
      // Defensive guard mirroring the checkbox's :disabled binding — a
      // disabled input shouldn't fire @change in practice, but this ensures
      // an unselectable candidate (already in group, or invalid phone) can
      // never end up in selectedIds through any code path.
      if (!this.selectedIds.has(studentId)) {
        const c = this.candidates.find(x => x.student_id === studentId);
        if (c && !this.isSelectable(c)) return;
      }
      if (this.selectedIds.has(studentId)) this.selectedIds.delete(studentId);
      else this.selectedIds.add(studentId);
      // Force reactivity — Set mutations aren't tracked by Vue's reactivity
      // system the way plain object/array mutations are.
      this.selectedIds = new Set(this.selectedIds);
    },

    selectRemaining() {
      const next = new Set(this.selectedIds);
      for (const c of this.candidates) {
        if (this.isSelectable(c)) next.add(c.student_id);
      }
      this.selectedIds = next;
    },

    selectVisible() {
      const next = new Set(this.selectedIds);
      for (const c of this.filteredCandidates) {
        if (this.isSelectable(c)) next.add(c.student_id);
      }
      this.selectedIds = next;
    },

    clearSelection() {
      this.selectedIds = new Set();
    },

    async startAdding() {
      // Defense in depth (requirement: "before sending the request to the
      // gateway, filter invalid phones") — selectedIds should already only
      // ever contain selectable (green, not-already-a-member) candidates via
      // isSelectable()'s checkbox guard, but this is the last checkpoint
      // before anything is sent, in case selectedIds was ever populated any
      // other way. The backend enforces this independently regardless.
      const selected = this.candidates.filter(c => this.selectedIds.has(c.student_id) && this.isSelectable(c));
      this.selectedTotal = selected.length;
      this.addedCount = 0;
      this.alreadyInGroupCount = 0;
      this.invalidPhoneCount = 0;
      this.noWhatsappCount = 0;
      this.privacyRestrictedCount = 0;
      this.failedCount = 0;
      this.checkFailedCount = 0;
      this.skippedCount = 0;
      this.currentBatchIndex = 0;
      this.log = [];

      const batches = [];
      for (let i = 0; i < selected.length; i += this.batchSize) {
        batches.push(selected.slice(i, i + this.batchSize));
      }
      this.totalBatches = batches.length;
      this.step = 'progress';

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        this.currentBatchIndex = i + 1;

        let batchFailed = false;
        let restricted = false;

        try {
          const { data } = await axios.post(`/student-groups/${this.groupId}/whatsapp/manual-add`, {
            student_ids: batch.map(c => c.student_id),
          });

          if (!data.status) {
            batchFailed = true;
            this.log.push({ type: 'fail', icon: '❌', text: data.message || 'فشلت الدفعة بالكامل' });
          } else {
            // Full outcome taxonomy from the backend — every participant is
            // processed independently there, so this is purely a display
            // mapping, never a source of pausing/aborting logic except for
            // privacy_restricted (an account-level WhatsApp signal, the same
            // kind of thing worth pausing a manual run for).
            for (const r of data.data?.results ?? []) {
              if (r.outcome === 'added') {
                this.addedCount++;
                this.log.push({ type: 'ok', icon: '✔', text: `${r.name} — تمت الإضافة` });
              } else if (r.outcome === 'already_in_group') {
                this.alreadyInGroupCount++;
                this.log.push({ type: 'info', icon: 'ℹ', text: `${r.name} — موجود بالمجموعة بالفعل` });
              } else if (r.outcome === 'no_whatsapp') {
                this.noWhatsappCount++;
                this.log.push({ type: 'fail', icon: '❌', text: `${r.name} — الرقم غير مسجَّل على واتساب` });
              } else if (r.outcome === 'privacy_restricted') {
                this.privacyRestrictedCount++;
                restricted = true;
                this.log.push({ type: 'warn', icon: '⚠', text: `${r.name} — تقييد خصوصية من واتساب` });
              } else if (r.outcome === 'invalid_phone') {
                // The frontend already excludes non-green candidates from
                // selection before sending — this branch only fires if the
                // backend's own independent validation (the real enforcement
                // point) catches something the UI missed, e.g. stale data.
                this.invalidPhoneCount++;
                this.log.push({ type: 'fail', icon: '❌', text: `${r.name} — رقم غير صالح: ${r.reason || ''}` });
              } else if (r.outcome === 'check_failed') {
                // The provider's check-number call itself failed (timeout,
                // provider unavailable, etc.) for this one participant —
                // never attempted the add, and never stopped the rest.
                this.checkFailedCount++;
                this.log.push({ type: 'warn', icon: '⚠', text: `${r.name} — تعذّر التحقق من الرقم: ${r.reason || ''}` });
              } else {
                this.failedCount++;
                this.log.push({ type: 'fail', icon: '❌', text: `${r.name} — فشل: ${r.reason || ''}` });
              }
            }
          }
        } catch (error) {
          batchFailed = true;
          this.log.push({ type: 'fail', icon: '❌', text: error.response?.data?.message || 'فشلت الدفعة بالكامل' });
        }

        // Only interrupt the run for a whole-batch failure or an account-level
        // WhatsApp restriction signal — an ordinary per-student rejection
        // (❌) is logged and the loop continues automatically, otherwise a
        // single bad number would stop the entire manual add.
        if (batchFailed || restricted) {
          const confirmResult = await Swal.fire({
            title: 'حدثت مشكلة في هذه الدفعة',
            text: 'هل تريد المتابعة مع بقية الطلاب؟',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'نعم، متابعة',
            cancelButtonText: 'لا، إيقاف',
          });
          if (!confirmResult.isConfirmed) {
            const remaining = batches.slice(i + 1).reduce((sum, b) => sum + b.length, 0);
            this.skippedCount += remaining;
            this.log.push({ type: 'fail', icon: '⚠', text: `تم إيقاف العملية — تخطي ${remaining} طالب متبقٍ` });
            break;
          }
        }

        if (i < batches.length - 1) {
          await sleep(this.delaySeconds * 1000);
        }
      }

      this.step = 'summary';
    },

    finish() {
      this.$emit('completed');
      this.$emit('close');
    },
  },

  mounted() {
    this.fetchCandidates();
  },
};
</script>

<style scoped>
.mma-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}
.mma-dialog {
  width: 100%;
  max-width: 640px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  padding: 1rem;
}
.mma-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #eee;
  margin-bottom: 0.75rem;
}
.mma-body {
  overflow-y: auto;
  flex: 1;
}
.mma-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 0.75rem;
  border-top: 1px solid #eee;
  margin-top: 0.75rem;
}
.mma-list {
  max-height: 360px;
  overflow-y: auto;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
}
.mma-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f5f5f5;
}
.mma-row:last-child {
  border-bottom: none;
}
.mma-row--disabled {
  opacity: 0.7;
}
.mma-name {
  flex: 1;
}
.mma-phone {
  min-width: 110px;
}
.mma-digits-badge {
  font-size: 0.78rem;
  color: #6c757d;
  white-space: nowrap;
}
.mma-log {
  max-height: 260px;
  overflow-y: auto;
  background: #fafafa;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.9rem;
}
.mma-log-line {
  padding: 2px 0;
}
.mma-log-line--fail {
  color: #dc3545;
}
.mma-log-line--warn {
  color: #b8860b;
}
.mma-log-line--info {
  color: #6c757d;
}
.mma-summary-row {
  display: flex;
  justify-content: space-between;
  padding: 0.35rem 0;
}
</style>
